import {
  useCommissionList,
  COMMISSION_STATUS_LABELS,
  COMMISSION_TYPE_LABELS,
  COMMISSION_LEVEL_LABELS,
  ALL_COMMISSION_TYPES,
  ALL_COMMISSION_LEVELS,
  COMMISSION_RANGE_LABELS,
  ALL_COMMISSION_RANGES,
} from "../features/commissions";
import type {
  CommissionStatus,
  CommissionType,
  CommissionRange,
} from "../features/commissions";

const STATUS_STYLES: Record<CommissionStatus, string> = {
  pending: "bg-amber-50 text-amber-700",
  settled: "bg-green-50 text-green-700",
  cancelled: "bg-gray-100 text-gray-500",
};

function formatDateTime(iso: string | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function CommissionsPage() {
  const {
    items,
    total,
    page,
    totalPages,
    beneficiaryUserId,
    sourceUserId,
    level,
    type,
    range,
    loading,
    error,
    setPage,
    setBeneficiaryUserId,
    setSourceUserId,
    setLevel,
    setType,
    setRange,
    resetFilters,
    reload,
  } = useCommissionList();

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        查看所有邀请奖励记录，支持按受益人、来源用户、级别、类型和时间范围筛选。
      </p>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={beneficiaryUserId}
          onChange={(e) =>
            setBeneficiaryUserId(e.target.value.replace(/\D/g, ""))
          }
          placeholder="受益人 ID"
          className="h-9 w-32 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <input
          type="text"
          value={sourceUserId}
          onChange={(e) => setSourceUserId(e.target.value.replace(/\D/g, ""))}
          placeholder="来源用户 ID"
          className="h-9 w-32 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部级别</option>
          {ALL_COMMISSION_LEVELS.map((lv) => (
            <option key={lv} value={String(lv)}>
              {COMMISSION_LEVEL_LABELS[lv]}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部类型</option>
          {ALL_COMMISSION_TYPES.map((t) => (
            <option key={t} value={t}>
              {COMMISSION_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {ALL_COMMISSION_RANGES.map((value) => (
            <option key={value || "all"} value={value}>
              {COMMISSION_RANGE_LABELS[value as CommissionRange]}
            </option>
          ))}
        </select>
        <button
          onClick={resetFilters}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          重置
        </button>
        <button
          onClick={reload}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          刷新
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="py-10 text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button
            onClick={reload}
            className="text-sm text-primary hover:underline"
          >
            重试
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && !error && (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          {items.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">
              当前暂无邀请奖励记录。只有被邀请人任务奖励结算后，才会生成固定奖励或任务返佣数据。
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      受益人
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      来源用户
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      订单ID
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      级别
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      类型
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      金额(元)
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      创建时间
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      结算时间
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-gray-500 tabular-nums text-xs">
                        {item.id}
                      </td>
                      <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                        <span>{item.beneficiary_nickname || "-"}</span>
                        <span className="text-gray-400 text-xs ml-1">
                          #{item.beneficiary_user_id}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                        <span>{item.source_nickname || "-"}</span>
                        <span className="text-gray-400 text-xs ml-1">
                          #{item.source_user_id}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 tabular-nums text-xs">
                        {item.reward_order_id}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 text-xs">
                        {COMMISSION_LEVEL_LABELS[item.level] ??
                          `L${item.level}`}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 text-xs">
                        {COMMISSION_TYPE_LABELS[item.type as CommissionType] ??
                          item.type}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                        &yen;{item.amount_yuan}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            STATUS_STYLES[item.status as CommissionStatus] ??
                            "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {COMMISSION_STATUS_LABELS[
                            item.status as CommissionStatus
                          ] ?? item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
                        {formatDateTime(item.created_at)}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
                        {formatDateTime(item.settled_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2 text-sm text-gray-500">
            <span>共 {total} 条</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                上一页
              </button>
              <span className="text-sm tabular-nums">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
